import React, { useState, useMemo } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, LinearProgress, MenuItem, Stack, TextField, Tooltip,
  Typography, FormControl, InputLabel, Select, Tabs, Tab, Alert, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { SectionHeader, ProgressBar } from '../components/ui';
import {
  ROUTE_STATUS, PRIORITY, ITEM_STATUS, ITEM_TYPES, ROUTE_KIND,
} from '../utils/schema';
import {
  resolvePlacement,
  computeStageEstimatedHours,
  computeRouteEstimatedHours,
  searchResources,
  stageHasResource,
} from '../utils/learningResources';

function ItemRow({
  item, placementId, stageIndex, itemIndex, routeId, stageId,
  otherStages, onEdit, onDelete, onUpdate, onMoveToStage,
}) {
  const statusMeta = ITEM_STATUS[item.status] || ITEM_STATUS.pending;
  const isOverdue = item.targetDate && item.status !== 'completed'
    && item.targetDate < new Date().toISOString().slice(0, 10);

  return (
    <Draggable draggableId={placementId} index={itemIndex} type="item">
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            py: 1.25,
            px: 1.5,
            mb: 0.5,
            borderRadius: 1.5,
            bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
            border: '1px solid',
            borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
            transition: 'border-color 0.15s',
          }}
        >
          <Box {...provided.dragHandleProps} sx={{ pt: 0.5, cursor: 'grab', color: 'text.disabled' }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
                {ITEM_TYPES[item.type]?.icon} {item.title || 'Sin título'}
              </Typography>
              <Chip label={statusMeta.label} size="small" color={statusMeta.color} />
              <Chip label={`${Number(item.estimatedHours) || 0}h`} size="small" variant="outlined" />
              {item.targetDate && (
                <Chip
                  label={item.targetDate}
                  size="small"
                  variant="outlined"
                  color={isOverdue ? 'error' : 'default'}
                />
              )}
              {(item.progress > 0 || item.status === 'in_progress') && (
                <Chip label={`${item.progress || 0}%`} size="small" variant="outlined" color="primary" />
              )}
            </Stack>

            {(item.progress > 0 || item.status === 'in_progress') && (
              <LinearProgress
                variant="determinate"
                value={item.status === 'completed' ? 100 : (item.progress || 0)}
                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
              />
            )}

            {item.institution && (
              <Typography variant="caption" color="text.secondary">{item.institution}</Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.25} alignItems="center" flexWrap="wrap">
            {otherStages.length > 0 && (
              <TextField
                select
                size="small"
                value=""
                displayEmpty
                onChange={(e) => {
                  if (e.target.value) onMoveToStage(routeId, stageId, placementId, e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="" disabled>Mover a etapa</MenuItem>
                {otherStages.map((st, idx) => (
                  <MenuItem key={st.id} value={st.id}>
                    Etapa {idx + 1} — {st.title}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select size="small" value={item.status}
              onChange={(e) => onUpdate(routeId, stageId, placementId, {
                status: e.target.value,
                progress: e.target.value === 'completed' ? 100 : item.progress,
              })}
              sx={{ minWidth: 120 }}
            >
              {Object.entries(ITEM_STATUS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
            {item.url && (
              <Tooltip title="Abrir recurso">
                <IconButton size="small" component="a" href={item.url} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => onEdit(item, stageId, placementId)}><EditIcon fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(routeId, stageId, placementId)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Draggable>
  );
}

function StageBlock({
  stage, stageIndex, route, routeId, learningResources, expanded, onToggle,
  onEditStage, onDeleteStage, onAddItem, onEditItem, onDeleteItem, onUpdateItem, onMoveToStage,
}) {
  const showStageHeader = route.routeKind !== 'fast_track' || (route.stages?.length || 0) > 1;
  const stageHours = computeStageEstimatedHours(stage, learningResources);
  const otherStagesById = (route.stages || []).filter((st) => st.id !== stage.id);

  const resolvedItems = (stage.items || [])
    .map((placement, itemIndex) => ({
      placement,
      item: resolvePlacement(placement, learningResources),
      itemIndex,
    }));

  return (
    <Box sx={{ mb: 3 }}>
      {showStageHeader && (
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            mb: 1, py: 1, px: 1,
            borderLeft: '3px solid', borderColor: 'primary.main',
            bgcolor: 'action.hover', borderRadius: '0 8px 8px 0',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                Etapa {stageIndex + 1} — {stage.title}
              </Typography>
              <Chip label={`${stage.progress || 0}%`} size="small" color="primary" />
              <Chip label={`Total: ${stageHours}h`} size="small" variant="outlined" />
              {stage.deadline && <Chip label={`Meta: ${stage.deadline}`} size="small" variant="outlined" />}
              <Chip label={PRIORITY[stage.priority]?.label || 'Media'} size="small" variant="outlined" />
            </Stack>
            {stage.expectedOutcomes && expanded && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Resultados: {stage.expectedOutcomes}
              </Typography>
            )}
            {stage.description && expanded && (
              <Typography variant="caption" color="text.secondary" display="block">{stage.description}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={onToggle}><ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} /></IconButton>
            <IconButton size="small" onClick={() => onEditStage(stage)}><EditIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => onAddItem(stage.id)}><AddIcon fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={() => onDeleteStage(routeId, stage.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}

      {(expanded || !showStageHeader) && (
        <Droppable droppableId={`stage-${stage.id}`} type="item">
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ pl: showStageHeader ? 2 : 0, minHeight: 48 }}>
              {resolvedItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, pl: 1 }}>
                  Arrastra recursos aquí o agrega uno nuevo.
                </Typography>
              ) : (
                resolvedItems.map(({ placement, item, itemIndex }) => (
                  item ? (
                  <ItemRow
                    key={placement.id}
                    item={item}
                    placementId={placement.id}
                    itemIndex={itemIndex}
                    routeId={routeId}
                    stageId={stage.id}
                    otherStages={otherStagesById}
                    onEdit={onEditItem}
                    onDelete={onDeleteItem}
                    onUpdate={onUpdateItem}
                    onMoveToStage={onMoveToStage}
                  />
                  ) : (
                    <Draggable key={placement.id} draggableId={placement.id} index={itemIndex} type="item">
                      {(provided) => (
                        <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} sx={{ py: 1, px: 1.5, mb: 0.5, color: 'text.secondary' }}>
                          Recurso no encontrado (ID: {placement.resourceId})
                        </Box>
                      )}
                    </Draggable>
                  )
                ))
              )}
              {provided.placeholder}
              {!showStageHeader && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => onAddItem(stage.id)} sx={{ mt: 1 }}>
                  Agregar certificación
                </Button>
              )}
            </Box>
          )}
        </Droppable>
      )}
    </Box>
  );
}

export default function TrainingPlanPage({ activeRouteId: externalRouteId, onRouteChange }) {
  const routes = useGrowthPlannerStore((s) => s.routes);
  const learningResources = useGrowthPlannerStore((s) => s.learningResources);
  const migrationReport = useGrowthPlannerStore((s) => s.migrationReport);
  const reconcileLibrary = useGrowthPlannerStore((s) => s.reconcileLibrary);
  const dismissMigrationReport = useGrowthPlannerStore((s) => s.dismissMigrationReport);
  const addRoute = useGrowthPlannerStore((s) => s.addRoute);
  const addStage = useGrowthPlannerStore((s) => s.addStage);
  const updateStage = useGrowthPlannerStore((s) => s.updateStage);
  const deleteStage = useGrowthPlannerStore((s) => s.deleteStage);
  const addItem = useGrowthPlannerStore((s) => s.addItem);
  const addPlacement = useGrowthPlannerStore((s) => s.addPlacement);
  const updateItem = useGrowthPlannerStore((s) => s.updateItem);
  const deleteItem = useGrowthPlannerStore((s) => s.deleteItem);
  const reorderStages = useGrowthPlannerStore((s) => s.reorderStages);
  const reorderPlacements = useGrowthPlannerStore((s) => s.reorderPlacements);
  const movePlacement = useGrowthPlannerStore((s) => s.movePlacement);

  const [activeRouteId, setActiveRouteId] = useState(routes[0]?.id || '');
  const resolvedRouteId = externalRouteId || activeRouteId;
  const [expandedStages, setExpandedStages] = useState({});
  const [routeDialog, setRouteDialog] = useState(false);
  const [stageDialog, setStageDialog] = useState({ open: false, stageId: null });
  const [itemDialog, setItemDialog] = useState({ open: false, stageId: null, item: null, placementId: null });
  const [itemDialogTab, setItemDialogTab] = useState('new');
  const [libraryResource, setLibraryResource] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const showRecoveryNotice = migrationReport
    && !migrationReport.dismissed
    && ((migrationReport.mergedGroups?.length || 0) > 0 || (migrationReport.inlineConverted || 0) > 0);
  const [routeForm, setRouteForm] = useState({
    name: '', targetRole: '', objective: '', targetDate: '', routeKind: 'standard',
  });
  const [stageForm, setStageForm] = useState({
    title: '', deadline: '', expectedOutcomes: '', priority: 'medium',
  });
  const [itemForm, setItemForm] = useState({
    type: 'certification', title: '', institution: '', url: '',
    targetDate: '', status: 'pending', progress: 0, estimatedHours: '',
  });

  const activeRoute = useMemo(
    () => routes.find((r) => r.id === resolvedRouteId) || routes[0],
    [routes, resolvedRouteId]
  );

  const routeTotalHours = useMemo(
    () => (activeRoute ? (activeRoute.estimatedHours ?? computeRouteEstimatedHours(activeRoute, learningResources)) : 0),
    [activeRoute, learningResources]
  );

  const libraryOptions = useMemo(
    () => searchResources(learningResources, ''),
    [learningResources]
  );

  const handleRouteSelect = (routeId) => {
    setActiveRouteId(routeId);
    onRouteChange?.(routeId);
  };

  React.useEffect(() => {
    if (externalRouteId) setActiveRouteId(externalRouteId);
  }, [externalRouteId]);

  React.useEffect(() => {
    if (routes.length && !routes.find((r) => r.id === resolvedRouteId)) {
      const fallback = routes[0].id;
      setActiveRouteId(fallback);
      onRouteChange?.(fallback);
    }
  }, [routes, resolvedRouteId, onRouteChange]);

  React.useEffect(() => {
    if (activeRoute?.stages) {
      const init = {};
      activeRoute.stages.forEach((s) => { init[s.id] = true; });
      setExpandedStages((prev) => ({ ...init, ...prev }));
    }
  }, [activeRoute?.id]);

  const toggleStage = (stageId) => {
    setExpandedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  const resetItemDialog = () => {
    setItemDialog({ open: false, stageId: null, item: null, placementId: null });
    setItemDialogTab('new');
    setLibraryResource(null);
    setDuplicateWarning('');
    setItemForm({
      type: 'certification', title: '', institution: '', url: '',
      targetDate: '', status: 'pending', progress: 0, estimatedHours: '',
    });
  };

  const openAddItemDialog = (stageId) => {
    resetItemDialog();
    setItemDialog({ open: true, stageId, item: null, placementId: null });
  };

  const handleCreateRoute = () => {
    if (!routeForm.name.trim()) return;
    addRoute(routeForm);
    setRouteForm({ name: '', targetRole: '', objective: '', targetDate: '', routeKind: 'standard' });
    setRouteDialog(false);
  };

  const handleSaveStage = () => {
    if (!stageForm.title.trim() || !activeRoute) return;
    if (stageDialog.stageId) {
      updateStage(activeRoute.id, stageDialog.stageId, stageForm);
    } else {
      addStage(activeRoute.id, stageForm);
    }
    setStageForm({ title: '', deadline: '', expectedOutcomes: '', priority: 'medium' });
    setStageDialog({ open: false, stageId: null });
  };

  const handleSaveItem = () => {
    if (!itemDialog.stageId || !activeRoute) return;
    setDuplicateWarning('');

    if (itemDialogTab === 'library' && !itemDialog.item) {
      if (!libraryResource) return;
      const stage = activeRoute.stages?.find((st) => st.id === itemDialog.stageId);
      if (stageHasResource(stage, libraryResource.id)) {
        setDuplicateWarning('Este recurso ya está en esta etapa.');
        return;
      }
      addPlacement(activeRoute.id, itemDialog.stageId, {
        resourceId: libraryResource.id,
        targetDate: itemForm.targetDate || '',
      });
      resetItemDialog();
      return;
    }

    if (!itemForm.title.trim()) return;

    const payload = {
      ...itemForm,
      estimatedHours: Number(itemForm.estimatedHours) || 0,
      progress: Number(itemForm.progress) || 0,
    };

    if (itemDialog.item && itemDialog.placementId) {
      updateItem(activeRoute.id, itemDialog.stageId, itemDialog.placementId, payload);
    } else {
      addItem(activeRoute.id, itemDialog.stageId, payload);
    }
    resetItemDialog();
  };

  const openEditItem = (item, stageId, placementId) => {
    setItemForm({
      type: item.type || 'certification',
      title: item.title || '',
      institution: item.institution || '',
      url: item.url || '',
      targetDate: item.targetDate || '',
      status: item.status || 'pending',
      progress: item.progress || 0,
      estimatedHours: item.estimatedHours || '',
    });
    setItemDialogTab('new');
    setLibraryResource(null);
    setDuplicateWarning('');
    setItemDialog({ open: true, stageId, item, placementId });
  };

  const handleMoveToStage = (routeId, fromStageId, placementId, toStageId) => {
    movePlacement(routeId, fromStageId, toStageId, placementId);
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !activeRoute) return;
    const { source, destination, type, draggableId } = result;
    if (type === 'stage') {
      reorderStages(activeRoute.id, source.index, destination.index);
    } else if (type === 'item') {
      const fromStageId = source.droppableId.replace('stage-', '');
      const toStageId = destination.droppableId.replace('stage-', '');
      const fromStage = activeRoute.stages?.find((st) => st.id === fromStageId);
      const sourceIndex = fromStage?.items?.findIndex((p) => p.id === draggableId) ?? -1;
      if (sourceIndex === -1) return;

      if (fromStageId === toStageId) {
        reorderPlacements(activeRoute.id, fromStageId, sourceIndex, destination.index);
      } else {
        movePlacement(activeRoute.id, fromStageId, toStageId, draggableId, destination.index);
      }
    }
  };

  const openStageDialog = (stage = null) => {
    if (stage) {
      setStageForm({
        title: stage.title, deadline: stage.deadline || '',
        expectedOutcomes: stage.expectedOutcomes || '', priority: stage.priority || 'medium',
      });
      setStageDialog({ open: true, stageId: stage.id });
    } else {
      setStageForm({ title: '', deadline: '', expectedOutcomes: '', priority: 'medium' });
      setStageDialog({ open: true, stageId: null });
    }
  };

  if (routes.length === 0) {
    return (
      <Box>
        <SectionHeader
          title="Plan de Formación"
          subtitle="Organiza etapas, certificaciones, plazos y prioridades de tu ruta profesional"
        />
        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" gutterBottom>
            Crea tu primera ruta para ver el plan completo con etapas y recursos.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRouteDialog(true)} sx={{ mt: 2 }}>
            Crear plan de formación
          </Button>
        </Card>
        <RouteDialog open={routeDialog} onClose={() => setRouteDialog(false)} form={routeForm} setForm={setRouteForm} onSave={handleCreateRoute} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionHeader
        title="Plan de Formación"
        subtitle="Vista resumida de tu ruta — arrastra para priorizar"
        action={
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Ruta activa</InputLabel>
              <Select
                value={activeRoute?.id || ''}
                label="Ruta activa"
                onChange={(e) => handleRouteSelect(e.target.value)}
              >
                {routes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                    {r.routeKind === 'fast_track' ? ' ⚡' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setRouteDialog(true)}>Nueva ruta</Button>
          </Stack>
        }
      />

      {showRecoveryNotice && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => dismissMigrationReport()}
          action={
            (migrationReport.mergedGroups?.length || 0) > 0 ? (
              <Button color="inherit" size="small" onClick={() => reconcileLibrary()}>
                Reconciliar de nuevo
              </Button>
            ) : null
          }
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Datos anteriores recuperados
          </Typography>
          {(migrationReport.inlineConverted || 0) > 0 && (
            <Typography variant="body2">
              {migrationReport.inlineConverted} recurso(s) del formato anterior convertidos al catálogo compartido.
            </Typography>
          )}
          {(migrationReport.mergedGroups || []).map((group) => (
            <Typography key={group.canonicalId} variant="body2">
              Unificado &quot;{group.title}&quot;
              {group.url ? ` (${group.url})` : ''}
              {' '}— {group.mergedCount} entradas duplicadas fusionadas en 1, con placements en sus etapas.
            </Typography>
          ))}
        </Alert>
      )}

      {activeRoute && (
        <>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
                    {activeRoute.name.toUpperCase()}
                  </Typography>
                  {activeRoute.targetRole && (
                    <Typography variant="body2" color="primary" fontWeight={500}>{activeRoute.targetRole}</Typography>
                  )}
                  {activeRoute.objective && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{activeRoute.objective}</Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={ROUTE_STATUS[activeRoute.status]?.label} color={ROUTE_STATUS[activeRoute.status]?.color} />
                  {activeRoute.routeKind === 'fast_track' && <Chip label="Ruta rápida" color="warning" variant="outlined" />}
                  {activeRoute.targetDate && <Chip label={`Meta: ${activeRoute.targetDate}`} variant="outlined" />}
                  <Chip label={`Plan: ${routeTotalHours}h total`} variant="outlined" color="info" />
                </Stack>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <ProgressBar value={activeRoute.progress || 0} label={`Progreso general — ${activeRoute.progress || 0}%`} />
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {activeRoute.routeKind === 'fast_track'
                ? 'Certificaciones prioritarias (arrastra para reordenar o mover entre etapas)'
                : 'Etapas del plan (arrastra etapas e items para priorizar)'}
            </Typography>
            {activeRoute.routeKind !== 'fast_track' && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => openStageDialog()}>
                Nueva etapa
              </Button>
            )}
          </Stack>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`route-${activeRoute.id}`} type="stage">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {(activeRoute.stages || []).map((stage, stageIndex) => (
                    <Draggable
                      key={stage.id}
                      draggableId={stage.id}
                      index={stageIndex}
                      type="stage"
                      isDragDisabled={activeRoute.routeKind === 'fast_track'}
                    >
                      {(stageProvided, snapshot) => (
                        <Box
                          ref={stageProvided.innerRef}
                          {...stageProvided.draggableProps}
                          sx={{
                            mb: 2,
                            opacity: snapshot.isDragging ? 0.9 : 1,
                          }}
                        >
                          {activeRoute.routeKind !== 'fast_track' && (
                            <Box
                              {...stageProvided.dragHandleProps}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5, cursor: 'grab', color: 'text.disabled' }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                              <Typography variant="caption">Arrastrar etapa</Typography>
                            </Box>
                          )}
                          <StageBlock
                            stage={stage}
                            stageIndex={stageIndex}
                            route={activeRoute}
                            routeId={activeRoute.id}
                            learningResources={learningResources}
                            expanded={expandedStages[stage.id] !== false}
                            onToggle={() => toggleStage(stage.id)}
                            onEditStage={openStageDialog}
                            onDeleteStage={deleteStage}
                            onAddItem={openAddItemDialog}
                            onEditItem={openEditItem}
                            onDeleteItem={deleteItem}
                            onUpdateItem={updateItem}
                            onMoveToStage={handleMoveToStage}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          {(activeRoute.stages || []).length === 0 && (
            <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary" gutterBottom>Agrega etapas a tu plan de formación</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openStageDialog()}>
                Etapa 1 — Fundamentos
              </Button>
            </Card>
          )}
        </>
      )}

      <RouteDialog open={routeDialog} onClose={() => setRouteDialog(false)} form={routeForm} setForm={setRouteForm} onSave={handleCreateRoute} />

      <Dialog open={stageDialog.open} onClose={() => setStageDialog({ open: false, stageId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{stageDialog.stageId ? 'Editar etapa' : 'Nueva etapa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" placeholder="Ej: Analytics corporativo" value={stageForm.title} onChange={(e) => setStageForm({ ...stageForm, title: e.target.value })} fullWidth autoFocus />
            <TextField label="Plazo / deadline" type="date" value={stageForm.deadline} onChange={(e) => setStageForm({ ...stageForm, deadline: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Resultados esperados" multiline rows={2} value={stageForm.expectedOutcomes} onChange={(e) => setStageForm({ ...stageForm, expectedOutcomes: e.target.value })} fullWidth />
            <TextField select label="Prioridad" value={stageForm.priority} onChange={(e) => setStageForm({ ...stageForm, priority: e.target.value })} fullWidth>
              {Object.entries(PRIORITY).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageDialog({ open: false, stageId: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveStage} disabled={!stageForm.title.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialog.open} onClose={resetItemDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{itemDialog.item ? 'Editar recurso' : 'Agregar al plan'}</DialogTitle>
        <DialogContent>
          {!itemDialog.item && (
            <Tabs value={itemDialogTab} onChange={(_, v) => { setItemDialogTab(v); setDuplicateWarning(''); }} sx={{ mb: 2 }}>
              <Tab value="new" label="Nuevo" />
              <Tab value="library" label="Biblioteca" />
            </Tabs>
          )}
          {duplicateWarning && <Alert severity="warning" sx={{ mb: 2 }}>{duplicateWarning}</Alert>}
          {itemDialogTab === 'library' && !itemDialog.item ? (
            <Stack spacing={2}>
              <Autocomplete
                options={libraryOptions}
                getOptionLabel={(opt) => `${ITEM_TYPES[opt.type]?.icon || ''} ${opt.title}${opt.institution ? ` — ${opt.institution}` : ''}`}
                value={libraryResource}
                onChange={(_, val) => setLibraryResource(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar en biblioteca" placeholder="Título, institución, tipo..." />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Stack>
                      <Typography variant="body2">{ITEM_TYPES[opt.type]?.icon} {opt.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[opt.institution, opt.type, `${opt.estimatedHours || 0}h`].filter(Boolean).join(' · ')}
                      </Typography>
                    </Stack>
                  </li>
                )}
                noOptionsText="No hay recursos en la biblioteca"
              />
              <TextField
                label="Plazo en esta etapa (opcional)"
                type="date"
                value={itemForm.targetDate}
                onChange={(e) => setItemForm({ ...itemForm, targetDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: itemDialog.item ? 1 : 0 }}>
              <TextField select label="Tipo" value={itemForm.type} onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })} fullWidth>
                {Object.entries(ITEM_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v.icon} {v.label}</MenuItem>)}
              </TextField>
              <TextField label="Título" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} fullWidth autoFocus />
              <TextField label="Plataforma / institución" value={itemForm.institution} onChange={(e) => setItemForm({ ...itemForm, institution: e.target.value })} fullWidth />
              <TextField label="URL del curso" value={itemForm.url} onChange={(e) => setItemForm({ ...itemForm, url: e.target.value })} fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField label="Plazo" type="date" value={itemForm.targetDate} onChange={(e) => setItemForm({ ...itemForm, targetDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
                <TextField select label="Estado" value={itemForm.status} onChange={(e) => setItemForm({ ...itemForm, status: e.target.value })} fullWidth>
                  {Object.entries(ITEM_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Progreso %" type="number" inputProps={{ min: 0, max: 100 }} value={itemForm.progress} onChange={(e) => setItemForm({ ...itemForm, progress: e.target.value })} fullWidth />
                <TextField label="Horas estimadas" type="number" value={itemForm.estimatedHours} onChange={(e) => setItemForm({ ...itemForm, estimatedHours: e.target.value })} fullWidth />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetItemDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveItem}
            disabled={itemDialogTab === 'library' && !itemDialog.item ? !libraryResource : !itemForm.title.trim()}
          >
            {itemDialogTab === 'library' && !itemDialog.item ? 'Agregar de biblioteca' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function RouteDialog({ open, onClose, form, setForm, onSave }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo plan de formación</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Nombre de la ruta" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth autoFocus />
          <TextField select label="Tipo de ruta" value={form.routeKind} onChange={(e) => setForm({ ...form, routeKind: e.target.value })} fullWidth>
            {Object.entries(ROUTE_KIND).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label} — {v.description}</MenuItem>
            ))}
          </TextField>
          <TextField label="Cargo / meta" value={form.targetRole} onChange={(e) => setForm({ ...form, targetRole: e.target.value })} fullWidth />
          <TextField label="Objetivo" multiline rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} fullWidth />
          <TextField label="Fecha meta" type="date" value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={onSave} disabled={!form.name.trim()}>Crear</Button>
      </DialogActions>
    </Dialog>
  );
}
